import { useState } from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useChangePasswordMutation } from '../../store/authApi';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';

// Forced password change for patients still on the default
// 'trustlab@2026' password. Auto-pops on the Reports page right
// after a successful UHID login when the auth response carries
// `must_change_password: true`. The modal is not closeable until
// the user successfully sets a new password — closing it would
// leave the account on the shared default.
//
// Props:
//   open       — controlled by parent
//   onSuccess  — () => void, called after the password is changed
//                so the parent can navigate forward
const ChangePasswordModal = ({ open, onSuccess }) => {
  const { user, updateUser } = useAuth();
  const { showToast } = useUI();
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const [pwd1, setPwd1] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    setErrMsg('');
    if (pwd1.length < 6)         return setErrMsg('Password must be at least 6 characters');
    if (pwd1 !== pwd2)           return setErrMsg('Passwords do not match');
    if (pwd1 === 'trustlab@2026') return setErrMsg('Choose a password different from the default');

    try {
      const res = await changePassword({ new_password: pwd1 }).unwrap();
      // Refresh the cached user so subsequent pages see the cleared
      // must_change_password flag. Uses updateUser (not setSession)
      // to keep the existing JWT — we don't want to log them out.
      updateUser(res.user);
      showToast('Password updated', 'success');
      setPwd1('');
      setPwd2('');
      onSuccess?.();
    } catch (err) {
      setErrMsg(err?.data?.message || 'Could not update password. Please try again.');
    }
  };

  return (
    <Modal
      open={open}
      // Disable closing — user MUST change the password to leave the
      // default. We pass no onClose so backdrop / Esc do nothing.
      disableEscapeKeyDown
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(21,42,30,0.55)' } } }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '92%', sm: 420 },
          bgcolor: '#fff',
          border: '1px solid var(--border-l)',
          borderRadius: '16px',
          boxShadow: '0 16px 48px rgba(21,42,30,0.16)',
          p: { xs: 2.75, md: 3.5 },
          outline: 'none',
        }}
        component="form"
        onSubmit={onSubmit}
      >
        {/* Lock icon banner */}
        <Box
          sx={{
            width: 48,
            height: 48,
            mx: 'auto',
            mb: 2,
            bgcolor: 'rgba(61,122,82,0.10)',
            border: '1px solid rgba(61,122,82,0.25)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 22, color: 'var(--sage)' }} />
        </Box>

        <Typography
          className="serif"
          sx={{
            fontSize: { xs: 22, md: 26 },
            fontWeight: 600,
            color: 'var(--ink)',
            textAlign: 'center',
            mb: 0.75,
            lineHeight: 1.2,
          }}
        >
          Change your password
        </Typography>
        <Typography
          sx={{
            fontSize: 13.5,
            color: 'var(--stone)',
            mb: 3,
            fontWeight: 300,
            textAlign: 'center',
            lineHeight: 1.55,
          }}
        >
          {user?.uhid
            ? <>You're currently using the default password for UHID <Box component="span" sx={{ fontWeight: 700, color: 'var(--ink)' }}>{user.uhid}</Box>. Please set a new one to continue.</>
            : 'You\'re currently using the default password. Please set a new one to continue.'}
        </Typography>

        {errMsg && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px' }}>
            {errMsg}
          </Alert>
        )}

        <TextField
          fullWidth
          label="New password"
          type={show1 ? 'text' : 'password'}
          value={pwd1}
          onChange={(e) => setPwd1(e.target.value)}
          disabled={isLoading}
          autoFocus
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShow1((v) => !v)} edge="end" aria-label={show1 ? 'Hide' : 'Show'}>
                  {show1
                    ? <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                    : <VisibilityOutlinedIcon    sx={{ fontSize: 20 }} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Confirm new password"
          type={show2 ? 'text' : 'password'}
          value={pwd2}
          onChange={(e) => setPwd2(e.target.value)}
          disabled={isLoading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShow2((v) => !v)} edge="end" aria-label={show2 ? 'Hide' : 'Show'}>
                  {show2
                    ? <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                    : <VisibilityOutlinedIcon    sx={{ fontSize: 20 }} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2.75 }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading}
          sx={{
            bgcolor: 'var(--forest)',
            color: '#fff',
            borderRadius: 100,
            py: 1.25,
            fontWeight: 700,
            textTransform: 'none',
            fontSize: 15,
            '&:hover': { bgcolor: 'var(--ink)' },
          }}
        >
          {isLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Update password'}
        </Button>

        <Typography
          sx={{
            fontSize: 11.5,
            color: 'var(--stone)',
            textAlign: 'center',
            mt: 2,
            fontWeight: 300,
          }}
        >
          Use at least 6 characters. Avoid using the default password.
        </Typography>
      </Box>
    </Modal>
  );
};

export default ChangePasswordModal;
